import { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { BACKEND_URL } from "../config/config";
import { NotificationContext } from "../components/context/NotificationWrapper";
import { useDebounce } from "./useDebounce";
import { handleMoneyChange } from "../utils/moneyInputUtils";

/**
 * Hook principal pour gérer la logique métier des formulaires de déclaration
 * Centralise :
 * - State management (salary, taxes, errors)
 * - Debouncing avec useRef pour sécurité monétaire
 * - Appels API (getTaxesAutocomplete, check-validity)
 * - Gestion des transitions attestation/facture
 *
 * @param {Object} options - Options de configuration
 * @param {Object} options.initialSalary - Valeurs initiales du salary
 * @param {Object} options.initialTaxes - Valeurs initiales des taxes
 * @param {string} options.membershipCode - Code adhésion de l'entreprise
 * @param {boolean} options.autoCalculate - Active le calcul auto des taxes (défaut: true)
 * @param {Object} options.form - Instance de formulaire Ant Design (optionnel)
 * @returns {Object} État et fonctions du formulaire
 */
export const useDeclarationForm = ({
  initialSalary = {},
  initialTaxes = {},
  membershipCode,
  autoCalculate = true,
  form = null,
}) => {
  const { openNotification } = useContext(NotificationContext);
  const backendUrl = BACKEND_URL;

  // State management
  const [salary, setSalary] = useState({
    salaryWorker: null,
    salaryEtam: null,
    salaryExecutive: null,
    workForce: null,
    month: null,
    year: null,
    agreeOptionalWorker: null,
    semester: null,
    ...initialSalary,
  });

  const [taxes, setTaxes] = useState({
    feeExecutive: 0,
    feeEtam: 0,
    feeWorker: 0,
    tvaExecutive: 0,
    tvaEtam: 0,
    tvaWorker: 0,
    feeAmount: 0,
    ...initialTaxes,
  });

  const [isInputLoading, setIsInputLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  // Abort controller pour annuler les requêtes en cours
  const abortControllerRef = useRef(null);

  // Debouncing pour sécurité monétaire
  const debouncedSalaryExecutive = useDebounce(salary.salaryExecutive);
  const debouncedSalaryEtam = useDebounce(salary.salaryEtam);
  const debouncedSalaryWorker = useDebounce(salary.salaryWorker);
  const debouncedWorkForce = useDebounce(salary.workForce);

  /**
   * Calcul automatique des taxes via API
   */
  const calculateTaxes = async () => {
    // Vérifier qu'on a au moins une valeur définie
    if (
      typeof debouncedSalaryExecutive === "undefined" &&
      typeof debouncedSalaryEtam === "undefined" &&
      typeof debouncedSalaryWorker === "undefined" &&
      typeof debouncedWorkForce === "undefined"
    ) {
      return;
    }

    const payload = {
      salaryWorker: salary?.salaryWorker,
      salaryEtam: salary?.salaryEtam,
      salaryExecutive: salary?.salaryExecutive,
      workForce: parseInt(salary?.workForce, 10) || 0,
      membershipCode: membershipCode,
    };

    setIsInputLoading(true);

    try {
      const response = await axios.post(
        `${backendUrl}/api/declaration/getTaxesAutocomplete`,
        payload,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const {
        feeExecutive,
        feeEtam,
        feeWorker,
        tvaExecutive,
        tvaEtam,
        tvaWorker,
        feeAmount,
        membershipCategory,
      } = response.data;

      setTaxes({
        feeExecutive,
        feeEtam,
        feeWorker,
        tvaExecutive,
        tvaEtam,
        tvaWorker,
        feeAmount,
      });

      // Mettre à jour le formulaire Ant Design si fourni
      if (form) {
        form.setFieldsValue({
          salaryExecutiveFee: feeExecutive,
          salaryEtamFee: feeEtam,
          salaryWorkerFee: feeWorker,
          salaryExecutiveTva: tvaExecutive,
          salaryEtamTva: tvaEtam,
          salaryWorkerTva: tvaWorker,
          feeAmount: feeAmount,
        });
      }

      return { taxes: response.data, membershipCategory };
    } catch (error) {
      console.error("Error calculating taxes:", error);
      if (openNotification) {
        openNotification(
          "Erreur",
          error.response?.data?.data || "Erreur lors du calcul des taxes",
          "error"
        );
      }
      throw error;
    } finally {
      setIsInputLoading(false);
    }
  };

  /**
   * Validation de la déclaration via API backend
   */
  const validateDeclaration = async (declarationData) => {
    // Annuler la requête précédente si elle existe
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    setErrors([]);

    const requestBody = {
      salaryWorker: declarationData?.salaryWorker || salary?.salaryWorker,
      salaryEtam: declarationData?.salaryEtam || salary?.salaryEtam,
      salaryExecutive: declarationData?.salaryExecutive || salary?.salaryExecutive,
      workForce: parseInt(declarationData?.workForce || salary?.workForce, 10),
      month: declarationData?.month || salary?.month,
      year: declarationData?.year || salary?.year,
      agreeOptionalWorker: declarationData?.agreeOptionalWorker ?? salary?.agreeOptionalWorker ?? false,
      membershipCode: declarationData?.membershipCode || membershipCode,
      id: declarationData?.id || salary?.id,
      isAnnual: declarationData?.isAnnual || false,
      semester: declarationData?.semester || null,
    };

    try {
      const response = await axios.post(
        `${backendUrl}/api/declaration/check-validity`,
        requestBody,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          signal: abortControllerRef.current?.signal,
        }
      );

      return response.data.data;
    } catch (error) {
      if (error.name === "AbortError") {
        return null;
      }

      // Gestion des erreurs
      const errorMessages = Array.isArray(error.response?.data?.data)
        ? error.response.data.data
        : [error.response?.data?.data || "Une erreur est survenue"];

      setErrors(errorMessages);
      throw error;
    }
  };

  /**
   * Handler pour les changements de valeurs du formulaire
   */
  const handleSalaryChange = (event) => {
    const { name, value, type, checked } = event.target;
    let newValue = type === "checkbox" ? checked : value;

    setSalary((prevSalary) => ({
      ...prevSalary,
      [name]: newValue,
    }));
  };

  /**
   * Handler spécifique pour les changements monétaires
   * Applique le formatage handleMoneyChange automatiquement
   */
  const handleMoneyFieldChange = (event) => {
    const formattedEvent = handleMoneyChange(event);
    handleSalaryChange(formattedEvent);
  };

  /**
   * Handler pour les changements de valeurs Ant Design Form
   */
  const handleFormValuesChange = (changedValues, allValues) => {
    const [key, value] = Object.entries(changedValues)[0];

    let processedValue = value;

    // Appliquer le formatage monétaire pour les champs salaire
    if (["salaryExecutive", "salaryEtam", "salaryWorker"].includes(key)) {
      processedValue = handleMoneyChange(value);
    }

    let updatedSalary = {
      ...salary,
      [key]: processedValue,
    };

    // Si workForce < 10, décocher automatiquement agreeOptionalWorker
    if (key === "workForce" && value < 10) {
      updatedSalary = {
        ...updatedSalary,
        agreeOptionalWorker: false,
      };
    }

    setSalary(updatedSalary);
  };

  /**
   * Handler pour changement de mois
   */
  const handleMonthChange = (monthValue) => {
    setSalary((prev) => ({ ...prev, month: monthValue }));
  };

  /**
   * Handler pour changement d'année
   */
  const handleYearChange = (selectedYear) => {
    const currentYear = new Date().getFullYear().toString();
    const currentMonth = new Date().getMonth() + 1;

    setSalary((prev) => {
      // Si l'année sélectionnée est l'année courante, vérifier si le mois sélectionné est valide
      if (selectedYear === currentYear && parseInt(prev.month) > currentMonth) {
        return {
          ...prev,
          year: selectedYear,
          month: currentMonth.toString(),
        };
      }
      return {
        ...prev,
        year: selectedYear,
      };
    });

    // Mettre à jour le formulaire Ant Design si fourni
    if (form) {
      form.setFieldsValue({ year: selectedYear });
    }
  };

  /**
   * Reset des erreurs
   */
  const clearErrors = () => {
    setErrors([]);
  };

  /**
   * Reset complet du formulaire
   */
  const resetForm = () => {
    setSalary({
      salaryWorker: null,
      salaryEtam: null,
      salaryExecutive: null,
      workForce: null,
      month: null,
      year: null,
      agreeOptionalWorker: null,
    });
    setTaxes({
      feeExecutive: 0,
      feeEtam: 0,
      feeWorker: 0,
      tvaExecutive: 0,
      tvaEtam: 0,
      tvaWorker: 0,
      feeAmount: 0,
    });
    setErrors([]);
  };

  /**
   * Mise à jour du state salary
   */
  const updateSalary = (newSalary) => {
    setSalary((prev) => ({ ...prev, ...newSalary }));
  };

  /**
   * Mise à jour du state taxes
   */
  const updateTaxes = (newTaxes) => {
    setTaxes((prev) => ({ ...prev, ...newTaxes }));
  };

  // Effect pour le calcul automatique des taxes
  useEffect(() => {
    if (autoCalculate && membershipCode) {
      calculateTaxes();
    }
  }, [
    debouncedSalaryExecutive,
    debouncedSalaryEtam,
    debouncedSalaryWorker,
    debouncedWorkForce,
  ]);

  return {
    // State
    salary,
    taxes,
    errors,
    isInputLoading,

    // Setters
    setSalary,
    setTaxes,
    setErrors,
    updateSalary,
    updateTaxes,

    // Handlers
    handleSalaryChange,
    handleMoneyFieldChange,
    handleFormValuesChange,
    handleMonthChange,
    handleYearChange,

    // Actions
    calculateTaxes,
    validateDeclaration,
    clearErrors,
    resetForm,

    // Refs
    abortControllerRef,
  };
};
